import type { GetStaticPaths, GetStaticProps } from "next"
import { PageMeta } from "@/components/common/Seo/PageMeta"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import {
  isResourceCategory,
  RESOURCE_CATEGORIES,
  type ResourceCategorySlug,
} from "@/components/resources/data/markdown/categories"
import {
  getAllTeasers,
  getPost,
  getPostSlugs,
  type MarkdownPost,
  relatedTeasers,
} from "@/components/resources/data/markdown/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { ArticlePage } from "@/components/resources/templates/ArticlePage"

const Post = ({ post, mdxSource, meta }: MarkdownPost) => (
  <>
    <PageMeta meta={meta} />
    <ArticlePage {...post}>
      <MarkdownContentRenderer source={mdxSource} />
    </ArticlePage>
  </>
)

Post.getLayout = SiteLayout

export default Post

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: (Object.keys(RESOURCE_CATEGORIES) as ResourceCategorySlug[]).flatMap((category) =>
    getPostSlugs(category).map((slug) => ({ params: { category, slug } })),
  ),
  fallback: false,
})

export const getStaticProps: GetStaticProps<
  MarkdownPost,
  { category: string; slug: string }
> = async ({ params }) => {
  const { category, slug } = params ?? {}
  if (!category || !slug || !isResourceCategory(category)) {
    return { notFound: true }
  }

  const post = await getPost(category, slug)
  if (!post) {
    return { notFound: true }
  }

  const related = relatedTeasers(await getAllTeasers(), post.post)

  return {
    props: { ...post, post: { ...post.post, related } },
    revalidate: 180,
  }
}
